import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getUserInfo, updateUserInfo } from "../../services/UserService";
import Cookies from "js-cookie";
import { clearStoredIdentity } from "../../services/sessionIdentity";

import "./Profile.css";
import {
  FiLogOut,
  FiEye,
  FiEyeOff,
  FiUser,
  FiMail,
  FiShield,
} from "react-icons/fi";
import Header from "../../components/Header/Header";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

const Profile = () => {
  const [user, setUser] = useState({ name: "", email: "" });

  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");

  const [errors, setErrors] = useState({
    name: "",
    email: "",
    password: "",
    currentPassword: "",
  });

  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const userInfo = await getUserInfo();
        setUser(userInfo);
      } catch (err) {
        console.error("Kullanıcı bilgileri alınamadı:", err);
        setMessageType("error");
        setMessage("Kullanıcı bilgileri alınamadı. Lütfen tekrar deneyin.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserInfo();
  }, []);

  const headerButtons = [
    {
      icon: <FiLogOut />,
      label: "Çıkış Yap",
      onClick: () => {
        clearStoredIdentity();
        Cookies.remove("token");
        navigate("/");
        window.location.reload();
      },
    },
  ];

  const validate = useMemo(
    () => ({
      name: (v) => {
        if (!v || String(v).trim().length < 2) {
          return "Ad en az 2 karakter olmalı.";
        }
        return "";
      },
      email: (v) => {
        if (!v || !emailRegex.test(String(v).trim())) {
          return "Geçerli bir e-posta girin.";
        }
        return "";
      },
      password: (v) => {
        if (v && String(v).length < 6) {
          return "Şifre en az 6 karakter olmalı.";
        }
        return "";
      },
      currentPassword: (current, next) => {
        if (next && !current) {
          return "Mevcut şifrenizi girin.";
        }
        return "";
      },
    }),
    []
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUser((prev) => ({ ...prev, [name]: value }));
    if (name === "name") setErrors((p) => ({ ...p, name: validate.name(value) }));
    if (name === "email") setErrors((p) => ({ ...p, email: validate.email(value) }));
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setErrors((p) => ({
      ...p,
      password: validate.password(value),
      currentPassword: validate.currentPassword(currentPassword, value),
    }));
  };

  const handleCurrentPasswordChange = (e) => {
    const value = e.target.value;
    setCurrentPassword(value);
    setErrors((p) => ({
      ...p,
      currentPassword: validate.currentPassword(value, password),
    }));
  };

  const togglePasswordVisibility = () => setShowPassword((p) => !p);
  const toggleCurrentPasswordVisibility = () =>
    setShowCurrentPassword((p) => !p);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    setMessage("");

    const nameErr = validate.name(user.name);
    const emailErr = validate.email(user.email);
    const passErr = validate.password(password);
    const currPassErr = validate.currentPassword(currentPassword, password);

    setErrors({
      name: nameErr,
      email: emailErr,
      password: passErr,
      currentPassword: currPassErr,
    });

    if (nameErr || emailErr) {
      setSubmitting(false);
      return;
    }

    const isTryingToChangePassword = Boolean(password);
    if (isTryingToChangePassword && (passErr || currPassErr)) {
      setMessageType("error");
      setMessage("Lütfen hatalı alanları düzeltin.");
      setSubmitting(false);
      return;
    }

    try {
      const result = await updateUserInfo({
        ...user,
        current_password: currentPassword ? currentPassword : undefined,
        password: isTryingToChangePassword ? password : undefined,
      });

      // Sunucudan dönen güncel bilgiyle senkronla (refresh sonrası da kalıcı)
      if (result?.user) {
        setUser((prev) => ({ ...prev, ...result.user }));
      }

      setMessageType("success");
      setMessage("Profil güncellendi!");

      setPassword("");
      setCurrentPassword("");
      setShowPassword(false);
      setShowCurrentPassword(false);
    } catch (err) {
      console.error("Profil güncellenemedi:", err);

      if (isTryingToChangePassword) {
        setMessageType("error");
        setMessage(
          "Bir hata oluştu. Mevcut şifre yanlış olabilir veya işlem yapılamadı."
        );
      } else {
        setMessage("");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const avatarChar = useMemo(
    () => String(user?.name || "U").charAt(0).toUpperCase(),
    [user?.name]
  );

  return (
    <div>
      <Header buttons={headerButtons} />

      <div className="profile-hero" aria-hidden="true">
        <div className="profile-hero-inner">
          <div className="profile-hero-main">
            <div className="profile-hero-avatar">{avatarChar}</div>
            <div className="profile-hero-copy">
              <span className="profile-hero-kicker">Buglog Hesabı</span>
              <h1 className="profile-hero-title">Profil</h1>
              <p className="profile-hero-subtitle">
                Hesap bilgilerinizi yönetin ve güvenlik tercihlerinizi özelleştirin.
              </p>
            </div>
          </div>

          <div className="profile-hero-panel">
            <div className="profile-hero-stat">
              <strong>{user?.name ? "1" : "0"}</strong>
              <span>Hesap</span>
            </div>
            <div className="profile-hero-stat">
              <strong>{user?.email ? "1" : "0"}</strong>
              <span>E-posta</span>
            </div>
            <div className="profile-hero-chip">
              {password ? "Şifre güncellemesi hazır" : "Bilgiler güncel"}
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="profile-skeleton">
          <div className="sk-row"></div>
          <div className="sk-grid">
            <div className="sk-card"></div>
            <div className="sk-card"></div>
          </div>
          <div className="sk-btn"></div>
        </div>
      ) : (
        <div className="profile-container profile-animate">
          {message && (
            <p
              className={`message ${
                messageType === "success"
                  ? "message-success"
                  : messageType === "error"
                    ? "message-error"
                    : "message-info"
              }`}
              role="status"
            >
              {message}
            </p>
          )}

          <form onSubmit={handleUpdate} noValidate>
            <div className="profile-grid">
              <section className="profile-section">
                <div className="section-head">
                  <FiUser className="section-icon" />
                  <h3 className="section-title">Hesap</h3>
                </div>

                <div className={`form-group ${errors.name ? "has-error" : ""}`}>
                  <label htmlFor="name">Ad</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={user.name}
                    onChange={handleChange}
                    required
                    autoComplete="name"
                    placeholder="Adınızı girin"
                    aria-invalid={!!errors.name}
                    aria-describedby={errors.name ? "err-name" : undefined}
                  />
                  {errors.name ? (
                    <small id="err-name" className="input-error">
                      {errors.name}
                    </small>
                  ) : (
                    <small className="input-hint">Görünen adınız.</small>
                  )}
                </div>

                <div className={`form-group ${errors.email ? "has-error" : ""}`}>
                  <label htmlFor="email" className="section-head email-label">
                    <FiMail className="section-icon" />
                    <span className="section-title">E-posta</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={user.email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                    placeholder="ornek@mail.com"
                    inputMode="email"
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? "err-email" : undefined}
                  />
                  {errors.email ? (
                    <small id="err-email" className="input-error">
                      {errors.email}
                    </small>
                  ) : (
                    <small className="input-hint">
                      Giriş yaparken kullanılır. Doğru olduğundan emin olun.
                    </small>
                  )}
                </div>
              </section>

              <section className="profile-section">
                <div className="section-head">
                  <FiShield className="section-icon" />
                  <h3 className="section-title">Güvenlik</h3>
                </div>

                <div className={`form-group ${errors.currentPassword ? "has-error" : ""}`}>
                  <label htmlFor="current-password">Mevcut Şifre</label>
                  <div className="password-field">
                    <input
                      type={showCurrentPassword ? "text" : "password"}
                      id="current-password"
                      name="current-password"
                      value={currentPassword}
                      onChange={handleCurrentPasswordChange}
                      autoComplete="current-password"
                      placeholder="Mevcut şifrenizi girin"
                      aria-invalid={!!errors.currentPassword}
                      aria-describedby={
                        errors.currentPassword ? "err-current-pass" : undefined
                      }
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={toggleCurrentPasswordVisibility}
                      aria-label={
                        showCurrentPassword ? "Şifreyi gizle" : "Şifreyi göster"
                      }
                      title={
                        showCurrentPassword ? "Şifreyi gizle" : "Şifreyi göster"
                      }
                    >
                      {showCurrentPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                  {errors.currentPassword ? (
                    <small id="err-current-pass" className="input-error">
                      {errors.currentPassword}
                    </small>
                  ) : (
                    <small className="input-hint">
                      Yeni şifre belirlerken zorunludur.
                    </small>
                  )}
                </div>

                <div className={`form-group ${errors.password ? "has-error" : ""}`}>
                  <label htmlFor="password">Yeni Şifre</label>
                  <div className="password-field">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={password}
                      onChange={handlePasswordChange}
                      autoComplete="new-password"
                      placeholder="Yeni şifrenizi girin (opsiyonel)"
                      minLength={6}
                      aria-invalid={!!errors.password}
                      aria-describedby={errors.password ? "err-pass" : undefined}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={togglePasswordVisibility}
                      aria-label={showPassword ? "Şifreyi gizle" : "Şifreyi göster"}
                      title={showPassword ? "Şifreyi gizle" : "Şifreyi göster"}
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                  {errors.password ? (
                    <small id="err-pass" className="input-error">
                      {errors.password}
                    </small>
                  ) : (
                    <small className="input-hint">
                      En az 6 karakter önerilir. Boş bırakırsanız şifreniz değişmez.
                    </small>
                  )}
                </div>
              </section>
            </div>

            <div className="form-actions-shell">
              <div className="form-actions-copy">
                <span className="form-actions-kicker">Kaydet</span>
                <strong>Profil değişikliklerini uygula</strong>
                <p>Ad, e-posta ve şifre alanlarındaki güncellemeler tek işlemde kaydedilir.</p>
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="update-button"
                  disabled={submitting}
                  aria-busy={submitting}
                >
                  {submitting ? "Güncelleniyor..." : "Güncelle"}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Profile;
